import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { RegistrationCivilUnionFormVM } from 'mutation/view-models/registration-civil-union';
import { transformCivilUnion } from 'notifications-from-civil-status/utils/transform-civil-union';
import RegistrationCivilUnionNonResidentPage from 'mutation/pages/registration-civil-union/non-resident';
import ErrorModal from 'shell/components/shared/modal-error';

interface RegistrationCivilUnionNonResidentNotificationSharedProps {
  isOther?: boolean;
  goBackUrl?: string;
  goBackUrlDetail?: string;
  items?: BreadcrumbItem[];
  editSipoUrl?: string;
}

const RegistrationCivilUnionNonResidentNotificationShared = ({
  goBackUrl = '',
  goBackUrlDetail = '',
  items = [],
  isOther,
  editSipoUrl = '',
}: RegistrationCivilUnionNonResidentNotificationSharedProps): ReactElement => {
  const [modalError, setModalError] = useState<{ message?: string; isError?: boolean }>({
    message: '',
    isError: false,
  });

  const params = useParams();

  const idNotifica = params.idNotifica || '';

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    RegistrationCivilUnionFormVM
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => {
      const castedRes = transformCivilUnion(res, true);
      if (castedRes.error) {
        // errori dal servizio
        setModalError({ message: castedRes.error, isError: true });
      }

      if (!castedRes.data.civilUnion?.idAnprSoggetto) {
        // mancanza dell'id anpr per il coniuge
        setModalError({
          message: 'Attenzione - manca idANPR del coniuge, effettuare la ricerca',
          isError: false,
        });
      }
      return castedRes.data;
    },
  });

  useEffect(() => {
    if (idAnsc && !isOther) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail);
    });
  });

  const onHandleExit = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <RegistrationCivilUnionNonResidentPage
          isWithoutBreadcrumbs
          initialValues={(dataEvent.data || {}) as RegistrationCivilUnionFormVM}
          onConfirm={onConfirm}
          onHandleExit={onHandleExit}
          disabledFromAct
          goBackUrl={goBackUrl}
          editSipoUrl={editSipoUrl}
        />
      ) : undefined}
      {Boolean(modalError.message) ? (
        <ErrorModal
          message={modalError.message || ''}
          onClose={() => {
            if (modalError.isError) {
              navigate(goBackUrl || '');
              return;
            }
            setModalError({ message: '', isError: false });
          }}
          open={Boolean(modalError)}
        />
      ) : undefined}
    </Fragment>
  );
};

RegistrationCivilUnionNonResidentNotificationShared.displayName =
  'Research CivilUnion Detail Notifications Page component';

export default RegistrationCivilUnionNonResidentNotificationShared;
